import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import axiosInstance from "../../axiosConfig";
import './ClubDashboard.css';
import ConversationsSection from './ConversationsSection';

// Hook for detecting mobile viewport
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

// Icons as simple SVG components
const Icons = {
  Crown: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"></path>
    </svg>
  ),
  Zap: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
    </svg>
  ),
  Users: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  ),
  Clock: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  ),
  Check: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  ),
  Message: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
  ),
  TrendingUp: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
      <polyline points="17 6 23 6 23 12"></polyline>
    </svg>
  ),
  MapPin: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
      <circle cx="12" cy="10" r="3"></circle>
    </svg>
  ),
  Send: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"></line>
      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
    </svg>
  ),
  X: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  ),
  ChevronRight: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"></polyline>
    </svg>
  ),
  Search: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  ),
  Home: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  ),
  Feed: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="17" y1="10" x2="3" y2="10"></line>
      <line x1="21" y1="6" x2="3" y2="6"></line>
      <line x1="21" y1="14" x2="3" y2="14"></line>
      <line x1="17" y1="18" x2="3" y2="18"></line>
    </svg>
  ),
  Map: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
      <line x1="8" y1="2" x2="8" y2="18"></line>
      <line x1="16" y1="6" x2="16" y2="22"></line>
    </svg>
  ),
  Forum: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
    </svg>
  ),
  ExternalLink: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
      <polyline points="15 3 21 3 21 9"></polyline>
      <line x1="10" y1="14" x2="21" y2="3"></line>
    </svg>
  ),
  Menu: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12"></line>
      <line x1="3" y1="6" x2="21" y2="6"></line>
      <line x1="3" y1="18" x2="21" y2="18"></line>
    </svg>
  ),
  ChevronLeft: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  ),
  LogOut: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
      <polyline points="16 17 21 12 16 7"></polyline>
      <line x1="21" y1="12" x2="9" y2="12"></line>
    </svg>
  ),
};

// Mobile Header Component
function MobileHeader({ clubData, onMenuClick }) {
  return (
    <header className="mobile-header">
      <button className="mobile-menu-btn" onClick={onMenuClick} aria-label="Open menu">
        <Icons.Menu />
      </button>
      <div className="mobile-header-title">
        <span className="mobile-logo">PrivateIQ</span>
        <span className="mobile-club-name">{clubData?.name || 'Dashboard'}</span>
      </div>
    </header>
  );
}

// Mobile Bottom Navigation Component
function MobileBottomNav({ activeSection, setActiveSection, stats }) {
  const navItems = [
    { id: "overview", label: "Overview", icon: <Icons.TrendingUp /> },
    { id: "nearby", label: "Discover", icon: <Icons.MapPin /> },
    { id: "pending", label: "Pending", icon: <Icons.Clock />, badge: stats?.pending || 0 },
    { id: "approved", label: "Approved", icon: <Icons.Check />, badge: stats?.approved || 0 },
    { id: "conversations", label: "Messages", icon: <Icons.Message />, badge: stats?.unreadMessages || 0 },
  ];

  return (
    <nav className="mobile-bottom-nav">
      {navItems.map((item) => (
        <button
          key={item.id}
          className={`mobile-nav-item ${activeSection === item.id ? 'active' : ''}`}
          onClick={() => setActiveSection(item.id)}
        >
          <span className="mobile-nav-icon">
            {item.icon}
            {item.badge > 0 && <span className="mobile-nav-badge">{item.badge}</span>}
          </span>
          <span className="mobile-nav-label">{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

// Stat Card Component
function StatCard({ icon, label, value, subValue, trend, color = "primary" }) {
  const colorClasses = {
    primary: "stat-card-primary",
    success: "stat-card-success",
    warning: "stat-card-warning",
    info: "stat-card-info",
  };

  return (
    <div className={`stat-card ${colorClasses[color]}`}>
      <div className="stat-card-icon">{icon}</div>
      <div className="stat-card-content">
        <span className="stat-card-label">{label}</span>
        <span className="stat-card-value">{value}</span>
        {subValue && <span className="stat-card-subvalue">{subValue}</span>}
        {trend && (
          <span className={`stat-card-trend ${trend > 0 ? 'positive' : 'negative'}`}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  );
}

// Sidebar Navigation Component
function Sidebar({ activeSection, setActiveSection, clubData, stats, isMobileOpen, onClose, isMobile }) {
  const navItems = [
    { id: "overview", label: "Overview", icon: <Icons.TrendingUp /> },
    { id: "nearby", label: "Discover Users", icon: <Icons.MapPin /> },
    { id: "pending", label: "Pending", icon: <Icons.Clock />, badge: stats?.pending || 0 },
    { id: "approved", label: "Approved", icon: <Icons.Check />, badge: stats?.approved || 0 },
    { id: "conversations", label: "Conversations", icon: <Icons.Message />, badge: stats?.unreadMessages || 0 },
  ];

  const exploreLinks = [
    { to: "/feestable", label: "Home", icon: <Icons.Home /> },
    { to: "/feed", label: "Feed", icon: <Icons.Feed /> },
    { to: "/courses", label: "Map", icon: <Icons.Map /> },
    { to: "/forums", label: "Forums", icon: <Icons.Forum /> },
  ];

  const handleNavClick = (sectionId) => {
    setActiveSection(sectionId);
    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isMobile && isMobileOpen && (
        <div className="sidebar-overlay" onClick={onClose} />
      )}

      <aside className={`dashboard-sidebar ${isMobile ? 'mobile' : ''} ${isMobileOpen ? 'open' : ''}`}>
        {/* Mobile close button */}
        {isMobile && (
          <button className="sidebar-close-btn" onClick={onClose}>
            <Icons.X />
          </button>
        )}

        <div className="sidebar-club-info">
          <h3>{clubData?.name || "Loading..."}</h3>
          <p>{clubData?.city}, {clubData?.state}</p>
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-nav-section">
            <span className="sidebar-nav-label">Dashboard</span>
            {navItems.map((item) => (
              <button
                key={item.id}
                className={`sidebar-nav-item ${activeSection === item.id ? "active" : ""}`}
                onClick={() => handleNavClick(item.id)}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
                {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
              </button>
            ))}
          </div>

          <div className="sidebar-nav-section">
            <span className="sidebar-nav-label">Explore PrivateIQ</span>
            {exploreLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="sidebar-nav-item explore-link"
                onClick={onClose}
              >
                <span className="nav-icon">{link.icon}</span>
                <span className="nav-label">{link.label}</span>
                <span className="external-icon"><Icons.ExternalLink /></span>
              </Link>
            ))}
          </div>
        </nav>

        <div className="sidebar-footer">
          <Link to="/logout" className="sidebar-logout-btn" onClick={onClose}>
            <Icons.LogOut />
            <span>Log Out</span>
          </Link>
          <div className="sidebar-contact">
            <p><strong>Need help?</strong></p>
            <p>support@privateiq.com</p>
          </div>
        </div>
      </aside>
    </>
  );
}

// Overview Section Component
function OverviewSection({ stats, recentActivity, clubData }) {
  return (
    <div className="overview-section">
      <div className="section-header">
        <h1>Dashboard Overview</h1>
        <p className="section-subtitle">Welcome back! Here's what's happening with your member outreach.</p>
      </div>

      <div className="stats-grid">
        <StatCard
          icon={<Icons.Users />}
          label="Total Requests Sent"
          value={stats?.total_requests_sent || 0}
          color="primary"
        />
        <StatCard
          icon={<Icons.Clock />}
          label="Pending Approval"
          value={stats?.pending || 0}
          color="warning"
        />
        <StatCard
          icon={<Icons.Check />}
          label="Approved Connections"
          value={stats?.approved || 0}
          color="success"
        />
        <StatCard
          icon={<Icons.Message />}
          label="Active Conversations"
          value={stats?.active_conversations || 0}
          color="info"
        />
      </div>

      <div className="overview-grid">
        <div className="overview-card conversion-funnel">
          <h3>Conversion Funnel</h3>
          <div className="funnel-chart">
            <div className="funnel-item">
              <div className="funnel-bar" style={{ width: '100%' }}>
                <span className="funnel-label">Requests Sent</span>
                <span className="funnel-value">{stats?.total_requests_sent || 0}</span>
              </div>
            </div>
            <div className="funnel-item">
              <div
                className="funnel-bar approved"
                style={{
                  width: stats?.total_requests_sent
                    ? `${((stats?.approved || 0) / stats?.total_requests_sent * 100)}%`
                    : '0%'
                }}
              >
                <span className="funnel-label">Approved</span>
                <span className="funnel-value">{stats?.approved || 0}</span>
              </div>
            </div>
            <div className="funnel-item">
              <div
                className="funnel-bar active"
                style={{
                  width: stats?.total_requests_sent
                    ? `${((stats?.active_conversations || 0) / stats?.total_requests_sent * 100)}%`
                    : '0%'
                }}
              >
                <span className="funnel-label">In Conversation</span>
                <span className="funnel-value">{stats?.active_conversations || 0}</span>
              </div>
            </div>
          </div>
          {stats?.total_requests_sent > 0 && (
            <div className="funnel-stats">
              <div className="funnel-stat">
                <span className="stat-value">
                  {((stats?.approved || 0) / stats?.total_requests_sent * 100).toFixed(1)}%
                </span>
                <span className="stat-label">Approval Rate</span>
              </div>
              <div className="funnel-stat">
                <span className="stat-value">
                  {stats?.approved > 0
                    ? ((stats?.active_conversations || 0) / stats?.approved * 100).toFixed(1)
                    : 0}%
                </span>
                <span className="stat-label">Engagement Rate</span>
              </div>
            </div>
          )}
        </div>

        <div className="overview-card quick-actions">
          <h3>Quick Actions</h3>
          <div className="action-buttons">
            <button className="action-btn primary" onClick={() => {}}>
              <Icons.MapPin />
              <span>Find New Prospects</span>
            </button>
            <button className="action-btn secondary" onClick={() => {}}>
              <Icons.Message />
              <span>View Messages</span>
            </button>
          </div>
          <div className="tips-section">
            <h4>💡 Tips for Success</h4>
            <ul>
              <li>Respond to approvals within 24 hours for best engagement</li>
              <li>Personalize your outreach based on member demographics</li>
              <li>Use the distance filter to target nearby prospects first</li>
            </ul>
          </div>
        </div>
      </div>

      {recentActivity && recentActivity.length > 0 && (
        <div className="recent-activity-card">
          <h3>Recent Activity</h3>
          <div className="activity-list">
            {recentActivity.slice(0, 5).map((activity, index) => (
              <div key={index} className="activity-item">
                <div className="activity-icon">
                  {activity.type === 'approved' ? <Icons.Check /> : <Icons.Clock />}
                </div>
                <div className="activity-content">
                  <p className="activity-text">{activity.message}</p>
                  <span className="activity-time">{activity.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Mobile User Card Component for Discover Tab - Clean Professional Design
function MobileUserCard({ user, type, onAction, actionLabel, actionIcon, formatIncomeRange, formatMaritalStatus, formatChildren }) {
  // Build primary stats line
  const primaryStats = [];
  if (user.age) primaryStats.push(`${user.age} years old`);
  if (user.household_income) primaryStats.push(formatIncomeRange(user.household_income));
  if (user.distance) primaryStats.push(`${user.distance.toFixed(1)} mi away`);

  // Build secondary info line
  const secondaryInfo = [];
  if (user.marital_status) secondaryInfo.push(formatMaritalStatus(user.marital_status));
  if (user.number_of_children) secondaryInfo.push(formatChildren(user.number_of_children));
  if (user.current_club_member === 'yes') secondaryInfo.push('Club member');

  return (
    <div className="mobile-prospect-card">
      {/* For approved users, show name header */}
      {type === 'approved' && user.full_name && (
        <div className="prospect-card-name">{user.full_name}</div>
      )}

      {/* Primary stats row */}
      <div className="prospect-card-stats">
        {primaryStats.map((stat, i) => (
          <span key={i}>
            {stat}
            {i < primaryStats.length - 1 && <span className="stat-separator">•</span>}
          </span>
        ))}
      </div>

      {/* Secondary info row */}
      {secondaryInfo.length > 0 && (
        <div className="prospect-card-details">
          {secondaryInfo.map((info, i) => (
            <span key={i}>
              {info}
              {i < secondaryInfo.length - 1 && <span className="stat-separator">•</span>}
            </span>
          ))}
        </div>
      )}

      {/* Email for approved users */}
      {type === 'approved' && user.email && (
        <a href={`mailto:${user.email}`} className="prospect-card-email">{user.email}</a>
      )}

      {/* Action button */}
      {onAction && (
        <button
          className="prospect-card-action"
          onClick={() => onAction(user.id || user.interaction_request_id)}
        >
          <span>{actionLabel}</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </button>
      )}
    </div>
  );
}

// Sort Icon Component
const SortIcon = ({ direction }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={`sort-icon ${direction || ''}`}
  >
    {direction === 'asc' ? (
      <path d="M12 5l0 14M5 12l7-7 7 7" />
    ) : direction === 'desc' ? (
      <path d="M12 19l0-14M5 12l7 7 7-7" />
    ) : (
      <>
        <path d="M7 8l5-5 5 5" opacity="0.4" />
        <path d="M7 16l5 5 5-5" opacity="0.4" />
      </>
    )}
  </svg>
);

// Filter Icon Component
const FilterIcon = ({ active }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill={active ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="2"
    className={`filter-icon ${active ? 'active' : ''}`}
  >
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

// User Table Component with Sorting and Filtering
function UserTable({ users, type, onAction, actionLabel, actionIcon }) {
  const isMobile = useIsMobile();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [filters, setFilters] = useState({});
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Format helpers
  const formatIncomeRange = (range) => {
    if (!range) return "N/A";
    if (range === "1000000+") return "$1M+";
    const [low, high] = range.split("-");
    const formattedHigh = high === "1000000" ? "$1M" : `$${(high / 1000).toFixed()}K`;
    return `$${(low / 1000).toFixed()}K - ${formattedHigh}`;
  };

  const formatMaritalStatus = (status) => {
    if (!status) return "N/A";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const formatChildren = (num) => {
    if (num === null || num === undefined) return "N/A";
    if (num === 0) return "No children";
    if (num === 1) return "1 child";
    return `${num} children`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get income sort value (for proper numeric sorting)
  const getIncomeSortValue = (range) => {
    if (!range) return 0;
    if (range === "1000000+") return 1000000;
    const [low] = range.split("-");
    return parseInt(low) || 0;
  };

  // Column definitions based on type
  const getColumns = () => {
    const baseColumns = [
      { key: 'age', label: 'Age', sortable: true, filterable: true, filterType: 'range' },
      { key: 'household_income', label: 'Income', sortable: true, filterable: true, filterType: 'select' },
      { key: 'marital_status', label: 'Marital', sortable: true, filterable: true, filterType: 'select' },
      { key: 'number_of_children', label: 'Children', sortable: true, filterable: true, filterType: 'select' },
      { key: 'current_club_member', label: 'Member', sortable: true, filterable: true, filterType: 'select' },
    ];

    if (type === "approved") {
      return [
        { key: 'full_name', label: 'Name', sortable: true, filterable: true, filterType: 'text' },
        { key: 'email', label: 'Contact', sortable: true, filterable: true, filterType: 'text' },
        ...baseColumns,
        { key: 'actions', label: 'Actions', sortable: false, filterable: false },
      ];
    } else if (type === "pending") {
      return [
        ...baseColumns,
        { key: 'distance', label: 'Distance', sortable: true, filterable: true, filterType: 'range' },
        { key: 'created_at', label: 'Requested', sortable: true, filterable: false },
        { key: 'actions', label: 'Actions', sortable: false, filterable: false },
      ];
    } else {
      // nearby users
      return [
        ...baseColumns,
        { key: 'distance', label: 'Distance', sortable: true, filterable: true, filterType: 'range' },
        { key: 'actions', label: 'Actions', sortable: false, filterable: false },
      ];
    }
  };

  const columns = getColumns();

  // Get unique values for select filters
  const getFilterOptions = (key) => {
    const values = users.map(u => u[key]).filter(v => v !== null && v !== undefined);
    const unique = [...new Set(values)];

    if (key === 'household_income') {
      return unique.sort((a, b) => getIncomeSortValue(a) - getIncomeSortValue(b));
    }
    if (key === 'number_of_children') {
      return unique.sort((a, b) => a - b);
    }
    return unique.sort();
  };

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') direction = 'desc';
      else if (sortConfig.direction === 'desc') direction = null;
    }
    setSortConfig({ key: direction ? key : null, direction });
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      if (value === '' || value === null) {
        const { [key]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: value };
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({});
    setActiveFilterColumn(null);
  };

  // Apply filters
  const filteredUsers = users.filter(user => {
    return Object.entries(filters).every(([key, filterValue]) => {
      if (!filterValue) return true;

      const value = user[key];

      if (key === 'age' || key === 'distance' || key === 'number_of_children') {
        const numValue = parseFloat(value) || 0;
        if (filterValue.min !== undefined && numValue < filterValue.min) return false;
        if (filterValue.max !== undefined && numValue > filterValue.max) return false;
        return true;
      }

      if (typeof filterValue === 'string') {
        const strValue = String(value || '').toLowerCase();
        return strValue.includes(filterValue.toLowerCase());
      }

      return value === filterValue;
    });
  });

  // Apply sorting
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortConfig.key || !sortConfig.direction) return 0;

    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];

    // Handle special cases
    if (sortConfig.key === 'household_income') {
      aVal = getIncomeSortValue(aVal);
      bVal = getIncomeSortValue(bVal);
    } else if (sortConfig.key === 'distance' || sortConfig.key === 'age' || sortConfig.key === 'number_of_children') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    } else if (sortConfig.key === 'created_at') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    } else {
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();
    }

    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Render filter dropdown
  const renderFilterDropdown = (column) => {
    if (!column.filterable || activeFilterColumn !== column.key) return null;

    const currentFilter = filters[column.key];

    if (column.filterType === 'select') {
      const options = getFilterOptions(column.key);
      return (
        <div className="filter-dropdown" onClick={e => e.stopPropagation()}>
          <div className="filter-dropdown-header">
            <span>Filter by {column.label}</span>
            <button className="filter-clear-btn" onClick={() => handleFilterChange(column.key, '')}>
              Clear
            </button>
          </div>
          <div className="filter-options">
            {options.map(opt => (
              <label key={opt} className="filter-option">
                <input
                  type="radio"
                  name={`filter-${column.key}`}
                  checked={currentFilter === opt}
                  onChange={() => handleFilterChange(column.key, opt)}
                />
                <span>
                  {column.key === 'household_income' ? formatIncomeRange(opt) :
                   column.key === 'marital_status' ? formatMaritalStatus(opt) :
                   column.key === 'current_club_member' ? (opt === 'yes' ? 'Member' : 'Non-member') :
                   column.key === 'number_of_children' ? formatChildren(opt) : opt}
                </span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (column.filterType === 'range') {
      return (
        <div className="filter-dropdown" onClick={e => e.stopPropagation()}>
          <div className="filter-dropdown-header">
            <span>Filter by {column.label}</span>
            <button className="filter-clear-btn" onClick={() => handleFilterChange(column.key, '')}>
              Clear
            </button>
          </div>
          <div className="filter-range">
            <input
              type="number"
              placeholder="Min"
              value={currentFilter?.min || ''}
              onChange={e => handleFilterChange(column.key, {
                ...currentFilter,
                min: e.target.value ? parseFloat(e.target.value) : undefined
              })}
            />
            <span>to</span>
            <input
              type="number"
              placeholder="Max"
              value={currentFilter?.max || ''}
              onChange={e => handleFilterChange(column.key, {
                ...currentFilter,
                max: e.target.value ? parseFloat(e.target.value) : undefined
              })}
            />
          </div>
        </div>
      );
    }

    if (column.filterType === 'text') {
      return (
        <div className="filter-dropdown" onClick={e => e.stopPropagation()}>
          <div className="filter-dropdown-header">
            <span>Filter by {column.label}</span>
            <button className="filter-clear-btn" onClick={() => handleFilterChange(column.key, '')}>
              Clear
            </button>
          </div>
          <input
            type="text"
            className="filter-text-input"
            placeholder={`Search ${column.label.toLowerCase()}...`}
            value={currentFilter || ''}
            onChange={e => handleFilterChange(column.key, e.target.value)}
            autoFocus
          />
        </div>
      );
    }

    return null;
  };

  // Render cell content
  const renderCell = (user, column) => {
    switch (column.key) {
      case 'full_name':
        return (
          <div className="user-name-cell">
            <div className="user-avatar">
              {(user.full_name || "U").charAt(0).toUpperCase()}
            </div>
            <span>{user.full_name || "Unknown"}</span>
          </div>
        );
      case 'email':
        return (
          <div className="contact-cell">
            <a href={`mailto:${user.email}`}>{user.email}</a>
            {user.phone_number && <span>{user.phone_number}</span>}
          </div>
        );
      case 'age':
        return <span className="badge badge-neutral">{user.age || "N/A"}</span>;
      case 'household_income':
        return <span className="income-value">{formatIncomeRange(user.household_income)}</span>;
      case 'marital_status':
        return (
          <span className={`badge badge-${user.marital_status === 'married' ? 'info' : 'neutral'}`}>
            {formatMaritalStatus(user.marital_status)}
          </span>
        );
      case 'number_of_children':
        return (
          <span className="children-value">
            {formatChildren(user.number_of_children)}
          </span>
        );
      case 'current_club_member':
        return (
          <span className={`badge ${user.current_club_member === "yes" ? "badge-success" : "badge-neutral"}`}>
            {user.current_club_member === "yes" ? "Member" : "Non-member"}
          </span>
        );
      case 'distance':
        return <span className="distance-value">{user.distance ? `${user.distance.toFixed(1)} mi` : "N/A"}</span>;
      case 'created_at':
        return <span className="date-value">{formatDate(user.created_at)}</span>;
      case 'actions':
        return onAction ? (
          <button
            className="table-action-btn"
            onClick={() => onAction(user.id || user.interaction_request_id)}
          >
            {actionIcon}
            <span>{actionLabel}</span>
          </button>
        ) : null;
      default:
        return user[column.key] || "N/A";
    }
  };

  if (!users || users.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">
          <Icons.Users />
        </div>
        <h3>No {type} found</h3>
        <p>
          {type === "nearby users"
            ? "Try expanding your search radius to find more prospects."
            : `You don't have any ${type} yet.`}
        </p>
      </div>
    );
  }

  const hasActiveFilters = Object.keys(filters).length > 0;

  // Quick sort handler - sets sort directly to descending (highest first)
  const quickSort = (key, direction = 'desc') => {
    setSortConfig({ key, direction });
  };

  // Mobile view with cards
  if (isMobile) {
    return (
      <div className="mobile-table-wrapper">
        {/* Mobile Toolbar */}
        <div className="mobile-table-toolbar">
          <div className="mobile-toolbar-top">
            <div className="mobile-result-count">
              <span className="count">{sortedUsers.length}</span>
              <span className="label">
                {sortedUsers.length !== users.length ? `of ${users.length}` : ''} prospects
              </span>
            </div>
            <div className="mobile-toolbar-actions">
              <button
                className={`mobile-toolbar-btn ${showMobileFilters ? 'active' : ''}`}
                onClick={() => setShowMobileFilters(!showMobileFilters)}
              >
                <FilterIcon active={hasActiveFilters} />
                <span>Filter</span>
                {hasActiveFilters && <span className="filter-count">{Object.keys(filters).length}</span>}
              </button>
            </div>
          </div>

          {/* Mobile Quick Sort Pills */}
          <div className="mobile-sort-pills">
            <span className="sort-pills-label">Sort by:</span>
            <div className="sort-pills-container">
              <button
                className={`sort-pill ${sortConfig.key === 'household_income' && sortConfig.direction === 'desc' ? 'active' : ''}`}
                onClick={() => quickSort('household_income', 'desc')}
              >
                Income High
              </button>
              <button
                className={`sort-pill ${sortConfig.key === 'household_income' && sortConfig.direction === 'asc' ? 'active' : ''}`}
                onClick={() => quickSort('household_income', 'asc')}
              >
                Income Low
              </button>
              <button
                className={`sort-pill ${sortConfig.key === 'age' && sortConfig.direction === 'desc' ? 'active' : ''}`}
                onClick={() => quickSort('age', 'desc')}
              >
                Age High
              </button>
              <button
                className={`sort-pill ${sortConfig.key === 'distance' && sortConfig.direction === 'asc' ? 'active' : ''}`}
                onClick={() => quickSort('distance', 'asc')}
              >
                Nearest
              </button>
              {sortConfig.key && (
                <button
                  className="sort-pill clear"
                  onClick={() => setSortConfig({ key: null, direction: null })}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Mobile Filter Panel */}
          {showMobileFilters && (
            <div className="mobile-filter-panel">
              <div className="mobile-filter-header">
                <h4>Filters</h4>
                {hasActiveFilters && (
                  <button className="clear-all-btn" onClick={clearAllFilters}>
                    Clear All
                  </button>
                )}
              </div>
              <div className="mobile-filter-grid">
                {/* Age Filter */}
                <div className="mobile-filter-item">
                  <label>Age Range</label>
                  <div className="mobile-filter-range">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.age?.min || ''}
                      onChange={e => handleFilterChange('age', {
                        ...filters.age,
                        min: e.target.value ? parseFloat(e.target.value) : undefined
                      })}
                    />
                    <span>-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.age?.max || ''}
                      onChange={e => handleFilterChange('age', {
                        ...filters.age,
                        max: e.target.value ? parseFloat(e.target.value) : undefined
                      })}
                    />
                  </div>
                </div>

                {/* Income Filter */}
                <div className="mobile-filter-item">
                  <label>Income</label>
                  <select
                    value={filters.household_income || ''}
                    onChange={e => handleFilterChange('household_income', e.target.value)}
                  >
                    <option value="">All</option>
                    {getFilterOptions('household_income').map(opt => (
                      <option key={opt} value={opt}>{formatIncomeRange(opt)}</option>
                    ))}
                  </select>
                </div>

                {/* Marital Status Filter */}
                <div className="mobile-filter-item">
                  <label>Marital Status</label>
                  <select
                    value={filters.marital_status || ''}
                    onChange={e => handleFilterChange('marital_status', e.target.value)}
                  >
                    <option value="">All</option>
                    {getFilterOptions('marital_status').map(opt => (
                      <option key={opt} value={opt}>{formatMaritalStatus(opt)}</option>
                    ))}
                  </select>
                </div>

                {/* Club Member Filter */}
                <div className="mobile-filter-item">
                  <label>Club Member</label>
                  <select
                    value={filters.current_club_member || ''}
                    onChange={e => handleFilterChange('current_club_member', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="yes">Members</option>
                    <option value="no">Non-members</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {hasActiveFilters && !showMobileFilters && (
            <div className="mobile-active-filters">
              {Object.entries(filters).map(([key, value]) => {
                const column = columns.find(c => c.key === key);
                let displayValue = value;
                if (key === 'household_income') displayValue = formatIncomeRange(value);
                else if (key === 'marital_status') displayValue = formatMaritalStatus(value);
                else if (key === 'current_club_member') displayValue = value === 'yes' ? 'Member' : 'Non-member';
                else if (typeof value === 'object') {
                  displayValue = `${value.min || '0'} - ${value.max || '∞'}`;
                }
                return (
                  <span key={key} className="mobile-filter-tag">
                    {column?.label}: {displayValue}
                    <button onClick={() => handleFilterChange(key, '')}>×</button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Mobile Cards */}
        <div className="mobile-cards-container">
          {sortedUsers.length > 0 ? (
            sortedUsers.map((user, index) => (
              <MobileUserCard
                key={user.id || index}
                user={user}
                type={type}
                onAction={onAction}
                actionLabel={actionLabel}
                actionIcon={actionIcon}
                formatIncomeRange={formatIncomeRange}
                formatMaritalStatus={formatMaritalStatus}
                formatChildren={formatChildren}
              />
            ))
          ) : (
            <div className="mobile-no-results">
              <p>No prospects match your filters</p>
              <button onClick={clearAllFilters}>Clear Filters</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop table view
  return (
    <div className="enhanced-table-wrapper">
      {/* Table toolbar */}
      <div className="table-toolbar">
        <div className="table-info">
          <span className="result-count">{sortedUsers.length}</span>
          <span className="result-label">
            {sortedUsers.length !== users.length
              ? `of ${users.length} prospects`
              : 'prospects'}
          </span>
        </div>

        {/* Quick Sort Buttons */}
        <div className="quick-sort-controls">
          <span className="quick-sort-label">Quick Sort:</span>
          <button
            className={`quick-sort-btn ${sortConfig.key === 'age' && sortConfig.direction === 'desc' ? 'active' : ''}`}
            onClick={() => quickSort('age', 'desc')}
            title="Sort by age (oldest first)"
          >
            Age ↓
          </button>
          <button
            className={`quick-sort-btn ${sortConfig.key === 'age' && sortConfig.direction === 'asc' ? 'active' : ''}`}
            onClick={() => quickSort('age', 'asc')}
            title="Sort by age (youngest first)"
          >
            Age ↑
          </button>
          <button
            className={`quick-sort-btn ${sortConfig.key === 'household_income' && sortConfig.direction === 'desc' ? 'active' : ''}`}
            onClick={() => quickSort('household_income', 'desc')}
            title="Sort by income (highest first)"
          >
            Income ↓
          </button>
          <button
            className={`quick-sort-btn ${sortConfig.key === 'household_income' && sortConfig.direction === 'asc' ? 'active' : ''}`}
            onClick={() => quickSort('household_income', 'asc')}
            title="Sort by income (lowest first)"
          >
            Income ↑
          </button>
          {sortConfig.key && (
            <button
              className="quick-sort-btn clear"
              onClick={() => setSortConfig({ key: null, direction: null })}
              title="Clear sort"
            >
              Clear
            </button>
          )}
        </div>

        {hasActiveFilters && (
          <button className="clear-filters-btn" onClick={clearAllFilters}>
            <Icons.X />
            Clear all filters
          </button>
        )}
      </div>

      {/* Active filters display */}
      {hasActiveFilters && (
        <div className="active-filters">
          {Object.entries(filters).map(([key, value]) => {
            const column = columns.find(c => c.key === key);
            let displayValue = value;
            if (key === 'household_income') displayValue = formatIncomeRange(value);
            else if (key === 'marital_status') displayValue = formatMaritalStatus(value);
            else if (key === 'current_club_member') displayValue = value === 'yes' ? 'Member' : 'Non-member';
            else if (typeof value === 'object') {
              displayValue = `${value.min || '0'} - ${value.max || '∞'}`;
            }
            return (
              <span key={key} className="filter-tag">
                {column?.label}: {displayValue}
                <button onClick={() => handleFilterChange(key, '')}>×</button>
              </span>
            );
          })}
        </div>
      )}

      <div className="modern-table-container">
        <table className="modern-table enhanced">
          <thead>
            <tr>
              {columns.map(column => (
                <th
                  key={column.key}
                  className={`
                    ${column.sortable ? 'sortable' : ''}
                    ${sortConfig.key === column.key ? 'sorted' : ''}
                    ${filters[column.key] ? 'filtered' : ''}
                  `}
                >
                  <div className="th-content">
                    <span
                      className="th-label"
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      {column.label}
                      {column.sortable && (
                        <SortIcon direction={sortConfig.key === column.key ? sortConfig.direction : null} />
                      )}
                    </span>
                    {column.filterable && (
                      <button
                        className="th-filter-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveFilterColumn(activeFilterColumn === column.key ? null : column.key);
                        }}
                      >
                        <FilterIcon active={!!filters[column.key]} />
                      </button>
                    )}
                  </div>
                  {renderFilterDropdown(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user, index) => (
              <tr key={user.id || index}>
                {columns.map(column => (
                  <td key={column.key}>{renderCell(user, column)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedUsers.length === 0 && users.length > 0 && (
        <div className="no-results">
          <p>No results match your filters.</p>
          <button onClick={clearAllFilters}>Clear filters</button>
        </div>
      )}
    </div>
  );
}

// Nearby Users Section
function NearbyUsersSection({ users, radius, setRadius, onRequestInfo, loading }) {
  return (
    <div className="content-section">
      <div className="section-header">
        <div>
          <h1>Discover Prospects</h1>
          <p className="section-subtitle">Find potential members near your club</p>
        </div>
      </div>

      <div className="filter-card">
        <div className="filter-row">
          <div className="filter-item radius-filter">
            <label>Search Radius: <strong>{radius} miles</strong></label>
            <Slider
              min={1}
              max={100}
              value={radius}
              onChange={setRadius}
              step={1}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Finding nearby prospects...</p>
        </div>
      ) : (
        <UserTable
          users={users}
          type="nearby users"
          onAction={onRequestInfo}
          actionLabel="Request Info"
          actionIcon={<Icons.Send />}
        />
      )}
    </div>
  );
}

// Pending Requests Section
function PendingRequestsSection({ requests, loading }) {
  return (
    <div className="content-section">
      <div className="section-header">
        <div>
          <h1>Pending Requests</h1>
          <p className="section-subtitle">Awaiting member approval</p>
        </div>
        <div className="section-stats">
          <span className="stat-badge warning">{requests?.length || 0} pending</span>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading pending requests...</p>
        </div>
      ) : (
        <UserTable
          users={requests}
          type="pending"
        />
      )}
    </div>
  );
}

// Approved Requests Section
function ApprovedRequestsSection({ requests, onStartConversation, loading }) {
  return (
    <div className="content-section">
      <div className="section-header">
        <div>
          <h1>Approved Connections</h1>
          <p className="section-subtitle">Members who want to connect with you</p>
        </div>
        <div className="section-stats">
          <span className="stat-badge success">{requests?.length || 0} approved</span>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading approved requests...</p>
        </div>
      ) : (
        <UserTable
          users={requests}
          type="approved"
          onAction={onStartConversation}
          actionLabel="Message"
          actionIcon={<Icons.Message />}
        />
      )}
    </div>
  );
}

// Subscription Banner Component
function SubscriptionBanner({ subscription, onUpgrade }) {
  if (!subscription) return null;

  const { is_subscribed, requests_used, free_request_limit, requests_remaining } = subscription;

  if (is_subscribed) {
    return (
      <div className="subscription-banner pro">
        <div className="banner-content">
          <Icons.Crown />
          <span><strong>Pro Plan</strong> - Unlimited member discovery requests</span>
        </div>
      </div>
    );
  }

  const isLimitReached = requests_remaining === 0;

  return (
    <div className={`subscription-banner ${isLimitReached ? 'warning' : 'free'}`}>
      <div className="banner-content">
        <Icons.Zap />
        <span>
          <strong>Free Plan:</strong> {requests_used} of {free_request_limit} requests used
          {!isLimitReached && ` (${requests_remaining} remaining)`}
        </span>
      </div>
      <button className="upgrade-btn" onClick={onUpgrade}>
        <Icons.Crown />
        Upgrade to Pro - $250/mo
      </button>
    </div>
  );
}

// Upgrade Modal Component
function UpgradeModal({ isOpen, onClose, onConfirm, loading }) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content upgrade-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <Icons.X />
        </button>
        <div className="upgrade-modal-header">
          <Icons.Crown />
          <h2>Upgrade to Pro</h2>
        </div>
        <div className="upgrade-modal-body">
          <p className="price">$250<span>/month</span></p>
          <ul className="features-list">
            <li><Icons.Check /> Unlimited member discovery requests</li>
            <li><Icons.Check /> Priority support</li>
            <li><Icons.Check /> Advanced analytics (coming soon)</li>
            <li><Icons.Check /> Cancel anytime</li>
          </ul>
        </div>
        <div className="upgrade-modal-footer">
          <button className="btn-secondary" onClick={onClose}>Maybe Later</button>
          <button className="btn-primary" onClick={onConfirm} disabled={loading}>
            {loading ? 'Processing...' : 'Continue to Checkout'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Dashboard Component
function ClubDashboard() {
  const { clubId } = useParams();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("overview");
  const [clubData, setClubData] = useState(null);
  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [stats, setStats] = useState({});
  const [error, setError] = useState("");
  const [radius, setRadius] = useState(20);
  const [loading, setLoading] = useState({
    nearby: false,
    pending: false,
    approved: false,
    conversations: false,
  });

  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Close mobile menu when section changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [activeSection]);

  // Check for subscription success/cancel from URL params
  useEffect(() => {
    const subscriptionStatus = searchParams.get('subscription');
    if (subscriptionStatus === 'success') {
      alert('Subscription activated successfully! You now have unlimited requests.');
      // Refresh subscription status
      fetchSubscriptionStatus();
    } else if (subscriptionStatus === 'cancelled') {
      // User cancelled checkout, no action needed
    }
  }, [searchParams]);

  // Fetch subscription status
  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axiosInstance.get('myclub/subscription/');
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription status:", error);
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  // Fetch club data
  useEffect(() => {
    const fetchClubData = async () => {
      try {
        const response = await axiosInstance.get(`myclub/dashboard/`, {
          params: { club_id: clubId },
        });
        setClubData(response.data);
      } catch (error) {
        setError("Failed to fetch club data. Please try again.");
      }
    };
    fetchClubData();
  }, [clubId]);

  // Fetch conversations and stats for overview
  useEffect(() => {
    const fetchConversationsAndStats = async () => {
      try {
        const response = await axiosInstance.get('club-interface/conversations/');
        setConversations(response.data.conversations || []);
        setStats(response.data.stats || {});
      } catch (error) {
        console.error("Failed to fetch conversations:", error);
      }
    };
    fetchConversationsAndStats();
  }, []);

  // Fetch data based on active section
  useEffect(() => {
    const fetchData = async () => {
      if (activeSection === "nearby") {
        setLoading(prev => ({ ...prev, nearby: true }));
        try {
          const response = await axiosInstance.get('myclub/nearby-users/', {
            params: { radius },
          });
          setNearbyUsers(response.data.nearby_users || []);
        } catch (error) {
          console.error("Failed to fetch nearby users:", error);
        }
        setLoading(prev => ({ ...prev, nearby: false }));
      } else if (activeSection === "pending") {
        setLoading(prev => ({ ...prev, pending: true }));
        try {
          const response = await axiosInstance.get('myclub/requests/pending/');
          setPendingRequests(response.data.requests || []);
        } catch (error) {
          console.error("Failed to fetch pending requests:", error);
        }
        setLoading(prev => ({ ...prev, pending: false }));
      } else if (activeSection === "approved") {
        setLoading(prev => ({ ...prev, approved: true }));
        try {
          const response = await axiosInstance.get('myclub/requests/approved/');
          setApprovedRequests(response.data.requests || []);
        } catch (error) {
          console.error("Failed to fetch approved requests:", error);
        }
        setLoading(prev => ({ ...prev, approved: false }));
      } else if (activeSection === "conversations") {
        setLoading(prev => ({ ...prev, conversations: true }));
        try {
          const response = await axiosInstance.get('club-interface/conversations/');
          setConversations(response.data.conversations || []);
        } catch (error) {
          console.error("Failed to fetch conversations:", error);
        }
        setLoading(prev => ({ ...prev, conversations: false }));
      }
    };
    fetchData();
  }, [activeSection, radius]);

  const handleRequestInfo = async (userId) => {
    try {
      const response = await axiosInstance.post('myclub/interactions/create', {
        user_id: userId,
      });
      // Remove user from nearby list after request
      setNearbyUsers(prev => prev.filter(u => u.id !== userId));
      // Update subscription state with new request counts
      if (response.data.requests_used !== undefined) {
        setSubscription(prev => ({
          ...prev,
          requests_used: response.data.requests_used,
          requests_remaining: response.data.requests_remaining,
          can_send_request: response.data.can_send_request,
        }));
      }
      alert(response.data.message || "Request sent successfully!");
    } catch (error) {
      // Check if upgrade is required
      if (error.response?.data?.upgrade_required) {
        setShowUpgradeModal(true);
      } else {
        alert(error.response?.data?.error || "Failed to send interaction request. Please try again.");
      }
    }
  };

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    try {
      const response = await axiosInstance.post('myclub/subscription/checkout/', {
        test_mode: true  // TODO: Remove for production - uses Stripe sandbox for testing
      });
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        alert("Failed to create checkout session. Please try again.");
      }
    } catch (error) {
      alert(error.response?.data?.error || "Failed to start checkout. Please try again.");
    }
    setCheckoutLoading(false);
  };

  const handleStartConversation = (requestId) => {
    const conv = conversations.find(c => c.interaction_request_id === requestId);
    if (conv) {
      setSelectedConversation(conv);
      setActiveSection("conversations");
    }
  };

  if (error) {
    return (
      <div className="dashboard-error">
        <Icons.X />
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className={`club-dashboard ${isMobile ? 'is-mobile' : ''}`}>
      {/* Mobile Header */}
      {isMobile && (
        <MobileHeader
          clubData={clubData}
          onMenuClick={() => setMobileMenuOpen(true)}
        />
      )}

      <Sidebar
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        clubData={clubData}
        stats={stats}
        isMobile={isMobile}
        isMobileOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      <main className="dashboard-main">
        {activeSection === "overview" && (
          <SubscriptionBanner
            subscription={subscription}
            onUpgrade={() => setShowUpgradeModal(true)}
          />
        )}

        {activeSection === "overview" && (
          <OverviewSection stats={stats} clubData={clubData} />
        )}
        {activeSection === "nearby" && (
          <NearbyUsersSection
            users={nearbyUsers}
            radius={radius}
            setRadius={setRadius}
            onRequestInfo={handleRequestInfo}
            loading={loading.nearby}
          />
        )}
        {activeSection === "pending" && (
          <PendingRequestsSection
            requests={pendingRequests}
            loading={loading.pending}
          />
        )}
        {activeSection === "approved" && (
          <ApprovedRequestsSection
            requests={approvedRequests}
            onStartConversation={handleStartConversation}
            loading={loading.approved}
          />
        )}
        {/* On mobile, render ConversationsSection here (inline) */}
        {activeSection === "conversations" && isMobile && (
          <ConversationsSection
            conversations={conversations}
            onSelectConversation={setSelectedConversation}
            selectedConversation={selectedConversation}
            loading={loading.conversations}
            sidebarWidth={0}
          />
        )}
      </main>

      {/* On desktop, render ConversationsSection via Portal to document.body */}
      {activeSection === "conversations" && !isMobile && (
        <ConversationsSection
          conversations={conversations}
          onSelectConversation={setSelectedConversation}
          selectedConversation={selectedConversation}
          loading={loading.conversations}
          sidebarWidth={window.innerWidth <= 1024 ? 88 : 280}
        />
      )}

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <MobileBottomNav
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          stats={stats}
        />
      )}

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onConfirm={handleUpgrade}
        loading={checkoutLoading}
      />
    </div>
  );
}

export default ClubDashboard;